import type {
  Retweet,
  SourceLink,
} from '~publish/legacy/composer/composer/entities/factories'
import type {
  ChannelData,
  Draft,
} from '~publish/legacy/composer/composer/entities/Draft'
import type { Post } from '~publish/legacy/composer/composer/entities/Draft/Draft'
import { AttachmentTypes } from '~publish/legacy/constants'

import { cloneOmniAsset } from '../postSlice/serialization'
import type { ChannelVariantContent, ThreadItem } from '../postSlice/types'
import { draftMediaToAssets } from './mapDraftToOmni'
import { projectFluxOmniAssets } from './projectFluxOmniContent'

/**
 * Recursively clone a plain-data `channelData` value: objects and arrays are
 * cloned at every level, primitives pass through, and `null`/`{}` are
 * preserved as-is. `channelData` is plain serialisable data (no class
 * instances), but the untyped `pinterest`/`bluesky` bags and the nested
 * `youtube.category` object mean a shallow or two-level clone would still
 * share references at some depth — recursing avoids special-casing those
 * shapes. The clone exists because the Zustand store is immer/auto-freeze:
 * writing in a shared reference would freeze Flux's live bag and break its
 * in-place edits.
 */
export function cloneChannelData(
  channelData: ChannelData | null,
): ChannelData | null {
  return cloneValue(channelData)
}

/** Same recursive-clone rationale as `cloneChannelData`, for `sourceLink`. */
export function cloneSourceLink(
  sourceLink: SourceLink | null,
): SourceLink | null {
  return cloneValue(sourceLink)
}

/**
 * Same recursive-clone rationale as `cloneChannelData`, for `retweet`: the
 * nested `media` array (and its `variants` sub-array) would otherwise share
 * references with the live Flux draft.
 */
export function cloneRetweet(retweet: Retweet | null): Retweet | null {
  return cloneValue(retweet)
}

// Overload declares the type-safe public shape (`T` in, `T` out); the
// implementation works over `unknown` so the recursion needs no assertion to
// satisfy it.
function cloneValue<T>(value: T): T
function cloneValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item))
  }
  if (value !== null && typeof value === 'object') {
    const clone: Record<string, unknown> = {}
    for (const [key, propertyValue] of Object.entries(value)) {
      clone[key] = cloneValue(propertyValue)
    }
    return clone
  }
  return value
}

/**
 * Project a Flux service draft's content into `ChannelVariantContent`. Reuses
 * `projectFluxOmniAssets` for the asset half and its deep-clone guarantee: the
 * Zustand store is immer/auto-freeze, so the projection must share no media
 * references with the live Flux draft, or writing it in would freeze the draft's
 * objects and break in-place Flux edits. `channelData` gets the same treatment
 * via `cloneChannelData`.
 *
 * `updateType`/`isReminder`/`title`/`enabledAttachmentType` normalize
 * identically to `useLegacyChannelData.buildSnapshot`'s reads of the same
 * service draft fields (that is the parity definition between the two):
 * `isReminder` is optional on `Draft` and defaults to `false`, the other
 * three are already `string | null`. `sourceLink` and `retweet` get the same
 * clone treatment as `channelData`; `selectedStickers` is a plain enum array,
 * so a shallow copy is enough.
 *
 * Pure: returns a new plain object every call; field identity is not preserved
 * (the mirror's diffing layer handles that).
 */
export function projectFluxChannelContent(draft: Draft): ChannelVariantContent {
  return {
    text: draft.text,
    urls: [...draft.urls],
    unshortenedUrls: [...draft.unshortenedUrls],
    assets: projectFluxOmniAssets(draft),
    aiAssisted: draft.aiAssisted,
    updateType: draft.updateType,
    isReminder: draft.isReminder ?? false,
    title: draft.title,
    enabledAttachmentType: draft.enabledAttachmentType,
    channelData: cloneChannelData(draft.channelData),
    sourceLink: cloneSourceLink(draft.sourceLink),
    retweet: cloneRetweet(draft.retweet),
    selectedStickers: [...draft.selectedStickers],
    threadItems: projectFluxThreadItems(draft),
  }
}

/**
 * Project a single legacy thread `Post` into a `ThreadItem`, applying the same
 * per-item link-wins gate as the save path (`mapThread.ts`'s
 * `hasLinkAttachment` derivation): a post's own `enabledAttachmentType` and
 * `link` decide whether its assets collapse to the link or fall through to its
 * media, independent of every other item's gate. Thread posts carry no
 * document, unlike the parent draft.
 */
function projectThreadPost(post: Post): ThreadItem {
  return {
    text: post.text,
    urls: [...post.urls],
    unshortenedUrls: [...post.unshortenedUrls],
    assets: draftMediaToAssets(
      {
        link: post.link,
        images: post.images,
        videos: post.videos,
        gif: post.gif,
      },
      {
        hasLinkAttachment:
          post.enabledAttachmentType === AttachmentTypes.LINK &&
          post.link !== null,
      },
    ).map(cloneOmniAsset),
  }
}

/**
 * Project a Flux draft's thread structure into `ThreadItem[]`, quarantining
 * legacy's `null | [1] | [2+]` tri-state to this one function: `null` becomes
 * `[]`, and every other case maps item-for-item. The projection is
 * deliberately not bijective — legacy `null` and a transient single-item array
 * both collapse to "not a thread" in the normalized space — so parity/drift
 * checks must compare in that normalized space, never against the raw legacy
 * shape.
 */
export function projectFluxThreadItems(draft: Draft): ThreadItem[] {
  if (draft.thread === null) return []
  return draft.thread.map(projectThreadPost)
}
